import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {
  Aangifte,
  BokkzIbDataService,
  IncomeTaxEntity,
  LoonInternationaleOrganisatie,
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {debounceTime, filter, map, Observable, pairwise, shareReplay, startWith, Subscription} from "rxjs";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type LoonInternationaleOrganisatieFormGroup = FormGroup<{
  loon: OmschrijvingEnBedragFormGroup;
  loonheffing: FormControl<number | null>;
  loonsoort: FormControl<string | null>;
  tegenwoordigeDienstbetrekking: FormControl<boolean | null>;
  deelPremieloonVolksverzekeringen: FormControl<number | null>;
  afdrachtZVWinNLmoetnog: FormControl<boolean | null>;
}>;

type OmschrijvingEnBedragFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-loon-internationale-organisatie',
  templateUrl: './loon-internationale-organisatie.component.html',
  styleUrls: ['./loon-internationale-organisatie.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiDetailsModule,
    CommonModule
  ]
})
export class LoonInternationaleOrganisatieComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public dialogVisible = false;
  public index = 0;
  public isPartner = false;

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public formGroup = new FormGroup({
    loonio: new FormArray<LoonInternationaleOrganisatieFormGroup>([]),
  });

  public optionsTegenwoordig: SelectFormControlItem<boolean>[] = [
    {value: true, label: "Tegenwoordige dienstbetrekking"},
    {value: false, label: "Vroegere dienstbetrekking"},
  ];

  public optionsZvw: SelectFormControlItem<boolean>[] = [
    {value: true, label: "Nederland"},
    {value: false, label: "Ander land"},
  ];

  ngOnInit(): void {
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        if (partner) {
          this.handleSwitch(entity, true);
        } else {
          this.handleSwitch(entity, false);
        }
      });
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: any): void {
    this.formGroup.reset({}, { emitEvent: false }); // Prevent reset from emitting events
    this.updateForm(details.box1?.belastbaarLoon?.vrijgesteldLoonInternationaleOrganisatie || []);
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const extractInputForCalculation = () => this.loonVanInternationaleOrganisatie.getRawValue().map(item => ({
      bedrag: item.loon.bedrag,
      loonheffing: item.loonheffing,
      afdrachtZVWinNLmoetnog: item.afdrachtZVWinNLmoetnog,
      deelPremieloonVolksverzekeringen: item.deelPremieloonVolksverzekeringen,
      tegenwoordigeDienstbetrekking: item.tegenwoordigeDienstbetrekking,
    }));

    this.changeSubscriptions.add(
      this.loonVanInternationaleOrganisatie.valueChanges.pipe(
        debounceTime(500),
        map(() => this.saveForm()),
        map(() => extractInputForCalculation()),
        startWith(extractInputForCalculation()),
        pairwise(),
        filter(([prev, curr]) => JSON.stringify(prev) !== JSON.stringify(curr)),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.dataService.calculateLatestIncomeTaxReturn().subscribe();
      })
    );
  }

  get loonVanInternationaleOrganisatie(): FormArray<LoonInternationaleOrganisatieFormGroup> {
    return this.formGroup.controls.loonio;
  }

  addRij(): void {
    this.loonVanInternationaleOrganisatie.push(this.createFormGroup(), { emitEvent: false });
    this.saveForm();
  }

  verwijderRij(index: number): void {
    this.loonVanInternationaleOrganisatie.removeAt(index, { emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false });
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
    this.subscribeToChanges();
  }

  createFormGroup(item?: LoonInternationaleOrganisatie): FormGroup {
    return new FormGroup({
      loon: new FormGroup({
        omschrijving: new FormControl<string | null>(item?.loon?.omschrijving ?? null),
        bedrag: new FormControl<number | null>(item?.loon?.bedrag ?? null),
      }),
      loonheffing: new FormControl<number | null>(item?.loonheffing ?? null),
      loonsoort: new FormControl<string | null>(item?.loonsoort ?? null),
      tegenwoordigeDienstbetrekking: new FormControl<boolean | null>(item?.tegenwoordigeDienstbetrekking ?? null),
      deelPremieloonVolksverzekeringen: new FormControl<number | null>(item?.deelPremieloonVolksverzekeringen ?? null),
      afdrachtZVWinNLmoetnog: new FormControl<boolean | null>(item?.afdrachtZVWinNLmoetnog ?? null),
    })
  }

  private updateForm(loon: LoonInternationaleOrganisatie[]): void {
    const lijst = loon || [];
    const formArray = this.loonVanInternationaleOrganisatie;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const formGroup = formArray.at(i) as FormGroup;
        formGroup.patchValue(lijst[i], { emitEvent: false });
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, { emitEvent: false });
      }
    }
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({emitEvent: false});

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const updateLoon = (aangifte: Aangifte) => ({
        ...aangifte,
        box1: {
          ...aangifte.box1,
          belastbaarLoon: {
            ...aangifte.box1?.belastbaarLoon,
            vrijgesteldLoonInternationaleOrganisatie: this.formGroup.getRawValue().loonio,
          },
        },
      });

      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: updateLoon(currentEntity.aangifte.partnerAangifte!),
      } : updateLoon(currentEntity.aangifte);

      return {
        ...currentEntity,
        aangifte: updatedAangifte,
      } as IncomeTaxEntity;
    });
  }
}
